import { lawyers, publicCases } from "./seed";
import {
  categoryLabels,
  stageLabels,
  type CaseCategory,
  type CaseProfile,
  type FitReport,
  type Lawyer,
  type LawyerFitResult,
  type LawyerProfile,
  type LawyerStats,
  type PublicCase,
  type ScoreDimension,
} from "./types";

const outcomeWeight: Record<string, number> = {
  無罪: 6,
  緩刑: 5,
  撤銷發回: 5,
  撤銷改判: 5,
  具保停止羈押: 5,
  量刑下降: 4,
  減刑: 4,
  和解: 3,
  易科罰金: 3,
  拘役: 2,
  上訴駁回: 0,
  程序裁定: 0,
};

export function getLawyerProfiles(sourceLawyers: Lawyer[] = lawyers, cases: PublicCase[] = publicCases): LawyerProfile[] {
  return sourceLawyers.map((lawyer) => {
    const lawyerCases = cases.filter((item) => item.lawyerId === lawyer.id);
    return {
      ...lawyer,
      cases: lawyerCases,
      stats: computeStats(lawyerCases),
    };
  });
}

export function getLawyerProfile(id: string): LawyerProfile | undefined {
  return getLawyerProfiles().find((lawyer) => lawyer.id === id);
}

export function searchLawyers(query: string): LawyerProfile[] {
  const normalized = query.trim().toLowerCase();
  const profiles = getLawyerProfiles();
  if (!normalized) return profiles;
  return profiles.filter((lawyer) => {
    const haystack = [
      lawyer.name,
      lawyer.officeName,
      lawyer.location,
      lawyer.guild ?? "",
      lawyer.barNo ?? "",
      lawyer.practiceTags.join(" "),
      lawyer.summary,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalized);
  });
}

export function createFitReport(profile: CaseProfile, sourceProfiles = getLawyerProfiles()): FitReport {
  const candidates = getCandidates(profile, sourceProfiles);
  const results = candidates
    .map((lawyer) => scoreLawyer(lawyer, profile))
    .sort((a, b) => b.totalScore - a.totalScore);
  const best = results[0];
  return {
    id: cryptoRandomId(),
    createdAt: new Date().toISOString(),
    request: profile,
    summary: best
      ? `此案件條件最接近 ${best.lawyer.name} 的公開紀錄，主要因為 ${best.strengths[0] ?? "案件類型與程序經驗較接近"}。`
      : "目前資料不足，無法產生候選律師比較。",
    disclaimer:
      "本報告依公開裁判書與示範資料推估適配度，不是法律意見，也不能保證結果。裁判結果受證據、案情、法官見解、程序階段與當事人條件影響，不能完全歸因於律師。",
    results,
  };
}

export function scoreLawyer(lawyer: LawyerProfile, profile: CaseProfile): LawyerFitResult {
  const cases = lawyer.cases;
  const categoryMatches = cases.filter((item) => item.category === profile.category);
  const stageMatches = cases.filter((item) => item.stage === profile.stage);
  const courtMatches = cases.filter((item) => item.court.includes(profile.courtRegion) || profile.courtRegion.includes(item.court));
  const recentCases = cases.filter((item) => new Date(item.date).getFullYear() >= 2024);
  const positiveCases = cases.filter((item) => item.outcomeTags.some((tag) => (outcomeWeight[tag] ?? 0) >= 4));
  const roleConfidence = cases.length > 0 ? Math.min(10, 6 + cases.length) : 0;

  const dimensions: ScoreDimension[] = [
    {
      key: "category",
      label: "案由匹配",
      score: cap(categoryMatches.length * 9 + relatedCategoryBonus(profile.category, cases), 25),
      max: 25,
      reason:
        categoryMatches.length > 0
          ? `找到 ${categoryMatches.length} 筆 ${categoryLabels[profile.category]} 相關公開紀錄。`
          : "尚未在示範資料中找到同案由公開紀錄。",
    },
    {
      key: "stage",
      label: "程序階段",
      score: cap(stageMatches.length * 6, 15),
      max: 15,
      reason:
        stageMatches.length > 0
          ? `有 ${stageMatches.length} 筆 ${stageLabels[profile.stage]} 階段紀錄。`
          : `尚未看到 ${stageLabels[profile.stage]} 階段紀錄。`,
    },
    {
      key: "court",
      label: "法院與地區",
      score: cap(courtMatches.length * 5, 10),
      max: 10,
      reason:
        courtMatches.length > 0 ? `有 ${courtMatches.length} 筆與 ${profile.courtRegion} 相關法院紀錄。` : "法院或地區經驗尚待面談確認。",
    },
    {
      key: "recency",
      label: "近期活躍度",
      score: cap(recentCases.length * 2.5, 10),
      max: 10,
      reason: recentCases.length > 0 ? `近三年有 ${recentCases.length} 筆公開紀錄。` : "近三年公開紀錄不足。",
    },
    {
      key: "outcome",
      label: "結果品質",
      score: cap(sumOutcomeScore(cases), 20),
      max: 20,
      reason: positiveCases.length > 0 ? `有 ${positiveCases.length} 筆含緩刑、撤銷、減刑或具保等結果標籤。` : "公開結果未顯示明顯有利標籤。",
    },
    {
      key: "role",
      label: "角色清晰度",
      score: roleConfidence,
      max: 10,
      reason: cases.length > 0 ? "公開資料中明確出現辯護人或法扶辯護人角色。" : "未能確認辯護人角色。",
    },
  ];

  const subtotal = dimensions.reduce((sum, item) => sum + item.score, 0);
  const riskPenalty = lawyer.riskFlags.length > 0 ? 4 : 0;
  const totalScore = Math.max(0, Math.round(subtotal - riskPenalty));
  const matchingCases = [...categoryMatches, ...stageMatches, ...courtMatches]
    .filter(uniqueById)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5);

  return {
    lawyer,
    totalScore,
    fitLevel: totalScore >= 72 ? "high" : totalScore >= 45 ? "medium" : "low",
    dimensions,
    matchingCases,
    strengths: buildStrengths(lawyer, profile, categoryMatches, stageMatches, recentCases, positiveCases),
    cautions: buildCautions(lawyer, profile, categoryMatches, stageMatches, matchingCases),
    interviewQuestions: buildInterviewQuestions(lawyer, profile),
  };
}

function computeStats(cases: PublicCase[]): LawyerStats {
  const categoryCounts = {} as Record<CaseCategory, number>;
  const courtCounts: Record<string, number> = {};
  const outcomeCounts: Record<string, number> = {};
  for (const item of cases) {
    categoryCounts[item.category] = (categoryCounts[item.category] ?? 0) + 1;
    courtCounts[item.court] = (courtCounts[item.court] ?? 0) + 1;
    for (const tag of item.outcomeTags) outcomeCounts[tag] = (outcomeCounts[tag] ?? 0) + 1;
  }
  return {
    totalPublicCriminalRoles: cases.length,
    recentRoleCount: cases.filter((item) => new Date(item.date).getFullYear() >= 2024).length,
    legalAidCount: cases.filter((item) => item.legalAid).length,
    categoryCounts,
    courtCounts,
    outcomeCounts,
  };
}

function getCandidates(profile: CaseProfile, profiles: LawyerProfile[]): LawyerProfile[] {
  if (profile.selectedLawyerIds?.length) {
    return profiles.filter((lawyer) => profile.selectedLawyerIds?.includes(lawyer.id));
  }
  return profiles;
}

function cap(value: number, max: number): number {
  return Math.min(max, Math.round(value));
}

function relatedCategoryBonus(category: CaseCategory, cases: PublicCase[]): number {
  const related: Record<CaseCategory, CaseCategory[]> = {
    fraud_money_laundering: ["financial_banking"],
    financial_banking: ["fraud_money_laundering"],
    violence_family: ["traffic_negligence"],
    traffic_negligence: ["violence_family"],
    drug: ["weapons"],
    weapons: ["drug"],
    sexual_offense: ["violence_family"],
    environmental: ["financial_banking"],
    other_criminal: [],
  };
  return cases.some((item) => related[category].includes(item.category)) ? 4 : 0;
}

function sumOutcomeScore(cases: PublicCase[]): number {
  return cases.reduce((sum, item) => {
    const bestTag = Math.max(0, ...item.outcomeTags.map((tag) => outcomeWeight[tag] ?? 0));
    const qualityBoost = item.resultQuality === "strong" ? 2 : item.resultQuality === "mixed" ? 1 : 0;
    return sum + bestTag + qualityBoost;
  }, 0);
}

function buildStrengths(
  lawyer: LawyerProfile,
  profile: CaseProfile,
  categoryMatches: PublicCase[],
  stageMatches: PublicCase[],
  recentCases: PublicCase[],
  positiveCases: PublicCase[],
): string[] {
  const strengths = [];
  if (categoryMatches.length) strengths.push(`有 ${categoryMatches.length} 筆 ${categoryLabels[profile.category]} 類型公開紀錄。`);
  if (stageMatches.length) strengths.push(`曾出現在 ${stageLabels[profile.stage]} 階段案件。`);
  if (recentCases.length) strengths.push(`近三年仍有公開刑事辯護紀錄，資料新鮮度較高。`);
  if (positiveCases.length) strengths.push(`公開結果中可見緩刑、撤銷、減刑或具保等較有利標籤。`);
  if (lawyer.stats.legalAidCount) strengths.push(`有 ${lawyer.stats.legalAidCount} 筆法扶角色紀錄，可能熟悉制度性刑事辯護流程。`);
  return strengths.length ? strengths : ["目前資料量有限，僅能作為初步面談前參考。"];
}

function buildCautions(
  lawyer: LawyerProfile,
  profile: CaseProfile,
  categoryMatches: PublicCase[],
  stageMatches: PublicCase[],
  matchingCases: PublicCase[],
): string[] {
  const cautions = [...lawyer.riskFlags];
  if (!categoryMatches.length) cautions.push(`未在目前資料中找到同屬 ${categoryLabels[profile.category]} 的公開紀錄。`);
  if (!stageMatches.length) cautions.push(`未在目前資料中看到 ${stageLabels[profile.stage]} 階段經驗，需面談確認。`);
  if (!matchingCases.length) cautions.push("候選律師與案件條件的交集不足，不建議只看分數決定委任。");
  cautions.push("裁判結果不能完全歸因於律師能力，仍需檢視證據、案情與委任策略。");
  return cautions;
}

function buildInterviewQuestions(lawyer: LawyerProfile, profile: CaseProfile): string[] {
  return [
    `你過去處理 ${categoryLabels[profile.category]} 案件時，通常優先評估哪些有利與不利證據？`,
    `若案件目前在「${stageLabels[profile.stage]}」階段，你會建議先爭取程序結果、認罪協商、緩刑，還是爭執構成要件？`,
    `你本人是否會負責開庭與書狀？若由團隊處理，分工方式是什麼？`,
    `費用是否涵蓋偵查、一審、二審或抗告？若程序延伸，如何計費？`,
    `${lawyer.name} 的公開紀錄中有部分案件屬法扶或示範資料，哪些案件最接近我的狀況？`,
  ];
}

function uniqueById(item: PublicCase, index: number, array: PublicCase[]): boolean {
  return array.findIndex((candidate) => candidate.id === item.id) === index;
}

function cryptoRandomId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `report_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}
